import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import { getBackendUrl } from '../config/environment';

const BACKEND_URL = getBackendUrl();

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [email, navigate]);

  const startCooldown = () => {
    setResendCooldown(60);
    intervalRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Le code doit contenir 6 chiffres');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        localStorage.setItem('token', data.access_token);
        toast.success('Email vérifié avec succès !');
        window.location.href = '/training';
      } else {
        setLoading(false);
        if (data.detail === 'INVALID_OR_EXPIRED_CODE') {
          toast.error('Code invalide ou expiré. Vérifiez votre email ou demandez un nouveau code.');
        } else {
          toast.error(data.detail || 'Erreur de vérification');
        }
      }
    } catch {
      setLoading(false);
      toast.error('Erreur de connexion au serveur');
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Un nouveau code a été envoyé à votre adresse email');
        startCooldown();
      } else if (data.detail === 'WAIT_BEFORE_RESEND') {
        toast.error('Veuillez patienter avant de demander un nouveau code');
        startCooldown();
      } else if (data.message === 'ALREADY_VERIFIED') {
        toast.success('Votre email est déjà vérifié !');
        navigate('/login');
      }
    } catch {
      toast.error('Erreur lors du renvoi');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #dbeafe 100%)' }}>
      <div className="w-full max-w-md">
        <div className="mb-4">
          <button
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 text-blue-700 hover:text-blue-900 transition-colors font-medium"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à la connexion</span>
          </button>
        </div>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#0c4a6e' }}>
            Coach DIA
          </h1>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-3">
              <Mail className="w-7 h-7 text-blue-600" />
            </div>
            <CardTitle className="text-xl" style={{ color: '#0c4a6e' }}>
              Vérifiez votre email
            </CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Un code à 6 chiffres a été envoyé à
            </p>
            <p className="text-sm font-semibold text-blue-700" data-testid="verification-email">
              {email}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Input
                  data-testid="verification-code-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                  autoFocus
                />
              </div>

              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading || code.length !== 6}
                data-testid="verify-email-btn"
              >
                {loading ? 'Vérification...' : 'Vérifier mon email'}
              </Button>

              <div className="text-center pt-2">
                <p className="text-xs text-gray-500 mb-2">
                  Vous n'avez pas reçu le code ?
                </p>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  className={`inline-flex items-center gap-1 text-sm font-medium transition-colors ${
                    resendCooldown > 0
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-blue-600 hover:text-blue-800'
                  }`}
                  data-testid="resend-code-btn"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  {resendCooldown > 0
                    ? `Renvoyer dans ${resendCooldown}s`
                    : 'Renvoyer le code'
                  }
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VerifyEmail;
